import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import { Message, Button, Confirm } from 'semantic-ui-react';

import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import dealershipBlacklistsQuery from '../graphql/dealershipBlacklistsQuery';
import createDealershipBlacklistMutation from '../graphql/createDealershipBlacklistMutation';
import updateDealershipBlacklistMutation from '../graphql/updateDealershipBlacklistMutation';
import deleteDealershipBlacklistMutation from '../graphql/deleteDealershipBlacklistMutation';

import Search from '../views/Shared/Search';
import DealershipBlacklistsNew from '../views/DealershipBlacklists/New';
import DealershipBlacklistsEdit from '../views/DealershipBlacklists/Edit';
import DealershipBlacklistsList from '../views/DealershipBlacklists/List';
import { setSearch } from '../data/searchField/actions';
import { setPage } from '../data/page/actions';

const DealershipBlacklists = props => {
  const {
    data,
    searchFields,
    setSearchFields,
    setPage: setPageAction,
    createMutation,
    updateMutation,
    deleteMutation,
  } = props;

  const { dealershipBlacklists, refetch, fetchMore } = data;

  const [flashMessage, setFlashMessage] = useState(null);
  const [selectedDealershipBlacklist, setSelectedDealershipBlacklist] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [newFormError, setNewFormError] = useState(null);
  const [newFormSubmitting, setNewFormSubmitting] = useState(null);
  const [editFormError, setEditFormError] = useState(null);
  const [editFormSubmitting, setEditFormSubmitting] = useState(null);

  const handleDismiss = () => {
    setFlashMessage(null);
  };

  const onSearchSubmitHandler = event => {
    const { dealerNumber } = event.target;

    const variableArguments = {
      dealerNumber: dealerNumber.value || null,
    };

    refetch({
      ...variableArguments,
    });

    setSearchFields(variableArguments);
    setPageAction({});
  };

  const onSelectDealershipBlacklist = (action, dealershipBlacklist) => {
    setSelectedDealershipBlacklist(dealershipBlacklist);

    switch (action) {
      case 'Edit':
        setIsEditModalOpen(true);
        break;
      case 'Delete':
        setIsDeleteConfirmationOpen(true);
        break;
      default:
        setSelectedDealershipBlacklist(null);
    }
  };

  const openNewForm = () => {
    setIsNewModalOpen(true);
  };

  const onNewFormCloseHandler = () => {
    setNewFormError(null);
    setNewFormSubmitting(null);
    setIsNewModalOpen(false);
  };

  const onNewFormSubmitHandler = values => {
    const input = { dealershipBlacklist: { ...values } };
    setNewFormSubmitting(true);

    createMutation({
      variables: { input },
    })
      .then(({ data: mutationData }) => {
        const { createDealershipBlacklist: { success, errors } } = mutationData;
        if (success) {
          setNewFormSubmitting(false);
          setFlashMessage('Successfully Created Dealership Blacklist');
          refetch();
          onNewFormCloseHandler();
        } else {
          setNewFormSubmitting(false);
          setNewFormError(errors);
        }
      })
      .catch(() => {
        setNewFormSubmitting(false);
        setNewFormError('Error while creating dealership blacklist');
      });
  };

  const onEditFormCloseHandler = () => {
    setEditFormError(null);
    setEditFormSubmitting(null);
    setIsEditModalOpen(false);
  };

  const onEditFormSubmitHandler = values => {
    const input = { dealershipBlacklist: { ...values } };
    setEditFormSubmitting(true);
    updateMutation({
      variables: { input },
    })
      .then(({ data: mutationData }) => {
        const { updateDealershipBlacklist: { success, errors } } = mutationData;
        if (success) {
          setEditFormSubmitting(false);
          setFlashMessage('Successfully Updated dealership blacklist');
          refetch();
          setPageAction({});
          onEditFormCloseHandler();
        } else {
          setEditFormSubmitting(false);
          setEditFormError(errors);
        }
      })
      .catch(error => {
        setEditFormSubmitting(false);
        setEditFormError(error);
      });
  };

  const handleDeleteConfirm = () => {
    const { id } = selectedDealershipBlacklist;
    const input = { dealershipBlacklistId: id };

    deleteMutation({
      variables: { input },
    })
      .then(({ data: mutationData }) => {
        const { deleteDealershipBlacklist: { success } } = mutationData;
        if (success) {
          setFlashMessage('Successfully Deleted dealership blacklist');

          fetchMore({
            updateQuery: (
              previousResult,
              { fetchMoreResult }
            ) => fetchMoreResult,
          });
          setPageAction({});
        } else {
          setFlashMessage('Deleting dealership blacklist not Success');
        }
        setIsDeleteConfirmationOpen(false);
      })
      .catch(() => {
        setIsDeleteConfirmationOpen(false);
        setFlashMessage('Deleting dealership blacklist not Success');
      });
  };

  const handleDeleteCancel = () => {
    setIsDeleteConfirmationOpen(false);
  };

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={handleDismiss}
          content={flashMessage}
          positive
        />
      )}

      <Button color='green' onClick={openNewForm} inverted>
        {' '}
        Create New Dealership Blacklist
      </Button>
      <br />
      <br />

      {isNewModalOpen && (
        <div>
          <DealershipBlacklistsNew
            isModalOpen={isNewModalOpen}
            onCloseHandler={onNewFormCloseHandler}
            onSubmitHandler={onNewFormSubmitHandler}
            error={newFormError}
            submitting={newFormSubmitting}
          />
        </div>
      )}

      {isEditModalOpen && (
        <div>
          <DealershipBlacklistsEdit
            isModalOpen={isEditModalOpen}
            onCloseHandler={onEditFormCloseHandler}
            onSubmitHandler={onEditFormSubmitHandler}
            error={editFormError}
            submitting={editFormSubmitting}
            dealershipBlacklist={selectedDealershipBlacklist}
          />
        </div>
      )}

      <Search
        onSubmit={onSearchSubmitHandler}
        searchFields={searchFields}
        searchOptions={['dealerNumber']}
      />

      <DealershipBlacklistsList
        onSelectDealershipBlacklist={onSelectDealershipBlacklist}
        dealershipBlacklists={dealershipBlacklists}
      />
      <Confirm
        open={isDeleteConfirmationOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

DealershipBlacklists.propTypes = {
  data: PropTypes.shape({
    dealershipBlacklists: PropTypes.array,
    refetch: PropTypes.func,
    fetchMore: PropTypes.func,
  }).isRequired,
  searchFields: PropTypes.object,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  createMutation: PropTypes.func.isRequired,
  updateMutation: PropTypes.func.isRequired,
  deleteMutation: PropTypes.func.isRequired,
};

DealershipBlacklists.defaultProps = {
  searchFields: {},
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const { data: { searchFields } } = state;
  return {
    searchFields,
  };
};

export default compose(
  withRouter,
  withPagination(dealershipBlacklistsQuery, 'dealershipBlacklists'),
  graphql(createDealershipBlacklistMutation, { name: 'createMutation' }),
  graphql(updateDealershipBlacklistMutation, { name: 'updateMutation' }),
  graphql(deleteDealershipBlacklistMutation, { name: 'deleteMutation' }),
  LoadingDataEnhancer,
  connect(mapStateToProps, mapDispatchToProps)
)(DealershipBlacklists);
